/**
 * Event activity set (`/me/events/:id/activities`).
 *
 * The activity list drives POI coverage and the time window, so it is edited on
 * its own route: participants also pick a subset of these, and mixing the two
 * selections on one screen is what the multi-level structure avoids.
 *
 * Each declared activity is also marked required or optional. Required ones are
 * the hard constraint a district must satisfy; optional ones only lower the
 * coverage score, weighted by how many members asked for them. That is what lets
 * a group say "dining for sure, KTV if it works out" without the KTV wish
 * removing every district from the plan.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ACTIVITIES, ACTIVITY_KEYS, type ActivityKey } from '@shared/activities'
import { LIMITS } from '@shared/constants'

import { ActivityRequirementPicker } from '../../../components/event/activity-requirement-picker'
import { ErrorAlert } from '../../../components/layout/states'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { useUpdateEvent } from '../../../hooks/use-events'
import { mutationErrorText } from '../../../lib/mutation-error'
import { activityLabelKey } from '../../../lib/format'
import { useEventDetailContext } from './layout'

export function EventActivitiesPage() {
  const { t } = useTranslation()
  const { event } = useEventDetailContext()
  const updateEvent = useUpdateEvent(event.id)

  const [selected, setSelected] = useState<ActivityKey[]>(event.activities)
  const [optional, setOptional] = useState<ActivityKey[]>(event.optionalActivities)
  const [localError, setLocalError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setSelected(event.activities)
    setOptional(event.optionalActivities)
  }, [event.id, event.updatedAt])

  function toggle(activity: ActivityKey) {
    setSelected((current) => {
      const next = current.includes(activity)
        ? current.filter((value) => value !== activity)
        : current.length >= LIMITS.maxActivitiesPerEvent
          ? current
          : ACTIVITY_KEYS.filter((key) => key === activity || current.includes(key))
      // An activity that is no longer declared cannot stay optional.
      setOptional((marks) => marks.filter((value) => next.includes(value)))
      return next
    })
    setSaved(false)
  }

  function submit() {
    if (selected.length === 0) {
      setLocalError(t('eventNew.activitiesError'))
      setSaved(false)
      return
    }
    setLocalError(null)
    updateEvent.mutate(
      { activities: selected, optionalActivities: optional },
      { onSuccess: () => setSaved(true) },
    )
  }

  const errorText = mutationErrorText(updateEvent.error, t)
  const atLimit = selected.length >= LIMITS.maxActivitiesPerEvent

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-4 sm:pt-5">
        <ChipGroup label={t('events.activities')}>
          {ACTIVITY_KEYS.map((activity) => (
            <Chip
              key={activity}
              selected={selected.includes(activity)}
              disabled={atLimit && !selected.includes(activity)}
              onClick={() => toggle(activity)}
            >
              <span aria-hidden>{ACTIVITIES[activity].icon}</span>
              {t(activityLabelKey(activity))}
            </Chip>
          ))}
        </ChipGroup>

        <p className="text-muted-foreground text-xs">
          {t('eventNew.activitiesHint', { max: LIMITS.maxActivitiesPerEvent })}
        </p>

        <ActivityRequirementPicker
          activities={selected}
          optional={optional}
          disabled={updateEvent.isPending}
          onChange={(next) => {
            setOptional(next)
            setSaved(false)
          }}
        />

        {localError ? (
          <ErrorAlert message={localError} />
        ) : null}

        {errorText ? (
          <ErrorAlert message={errorText} />
        ) : null}

        {saved ? (
          <Alert variant="success">
            <AlertDescription>{t('profile.saved')}</AlertDescription>
          </Alert>
        ) : null}

        <Button className="self-start" disabled={updateEvent.isPending} onClick={submit}>
          {t('common.save')}
        </Button>
      </CardContent>
    </Card>
  )
}
